import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { isPaymentGuaranteeType } from '@features/booking/state/types';
import { Private } from '@shared/components/common';

const PERU_COUNTRY_CODE = '+51';
const ARGENTINA_COUNTRY_CODE = '+54';

/**
 * Billing section for payment page — boleta/factura for Peru,
 * billing address + document for other countries.
 * Reads/writes directly from/to booking state.
 */
export function BillingSection() {
  const { t } = useTranslation('booking');
  const booking = useBooking();
  const restaurant = useRestaurant();

  const allowBillingDetails = !!restaurant.allowFullBillingDetails;
  const isPeruvianRestaurant = restaurant.countryCode === PERU_COUNTRY_CODE;
  const isArgentinianRestaurant = restaurant.countryCode === ARGENTINA_COUNTRY_CODE;
  const hasGuarantee = isPaymentGuaranteeType(booking.guaranteeType);
  const showBillingAddress = allowBillingDetails && hasGuarantee && !isPeruvianRestaurant;
  const showPeruBilling = isPeruvianRestaurant && hasGuarantee;
  const showDocumentField = (isPeruvianRestaurant || isArgentinianRestaurant) && allowBillingDetails && hasGuarantee;

  const [billingAddress, setBillingAddress] = useState(booking.billingAddress);
  const [identityDocument, setIdentityDocument] = useState(booking.identityDocument);
  const [billType, setBillType] = useState<'boleta' | 'factura'>(booking.billType || 'boleta');
  const [billPersonalName, setBillPersonalName] = useState(booking.billPersonalName);
  const [billPersonalDocument, setBillPersonalDocument] = useState(booking.billPersonalDocument);
  const [billPersonalAddress, setBillPersonalAddress] = useState(booking.billPersonalAddress);
  const [billBusinessName, setBillBusinessName] = useState(booking.billBusinessName);
  const [billBusinessDocument, setBillBusinessDocument] = useState(booking.billBusinessDocument);
  const [billBusinessAddress, setBillBusinessAddress] = useState(booking.billBusinessAddress);

  // Sync to booking state on blur (so payment submit has the data)
  const syncBilling = () => {
    const updates: Record<string, unknown> = {};
    if (showBillingAddress) {
      updates.billingAddress = billingAddress.trim();
      if (showDocumentField) {
        updates.identityDocument = identityDocument.trim();
      }
    }
    if (showPeruBilling) {
      updates.billType = billType;
      if (billType === 'boleta') {
        updates.billPersonalName = billPersonalName.trim();
        updates.billPersonalDocument = billPersonalDocument.trim();
        updates.billPersonalAddress = billPersonalAddress.trim();
      } else {
        updates.billBusinessName = billBusinessName.trim();
        updates.billBusinessDocument = billBusinessDocument.trim();
        updates.billBusinessAddress = billBusinessAddress.trim();
      }
    }
    if (Object.keys(updates).length > 0) {
      booking.updateBooking(updates);
    }
  };

  if (!showBillingAddress && !showPeruBilling) return null;

  return (
    // Wrap the entire billing region in <Private> so session-replay tools
    // never capture address / ID / tax-ID / business name. All 8 inputs
    // here collect PII; one annotation on the wrapper masks them all.
    <Private className="space-y-4" onBlur={syncBilling}>
      {/* Generic billing address (non-Peru) */}
      {showBillingAddress && (
        <div className="space-y-3">
          <label htmlFor="billing-address" className="text-sm font-medium text-foreground/70">{t('PAYMENT_LABEL_BILLING_ADDRESS')}</label>
          <input
            id="billing-address"
            type="text"
            aria-required="true"
            className="w-full rounded-xl border border-border p-3 text-foreground focus:border-brand focus:outline-none"
            placeholder={t('PERSONALIZE_BILLING_ADDRESS_PLACEHOLDER')}
            value={billingAddress}
            onChange={(e) => setBillingAddress(e.target.value)}
          />
          {showDocumentField && (
            <>
              <label htmlFor="identity-document" className="text-sm font-medium text-foreground/70">{t('PAYMENT_LABEL_IDENTITY_NUMBER')}</label>
              <input
                id="identity-document"
                type="text"
                aria-required="true"
                className="w-full rounded-xl border border-border p-3 text-foreground focus:border-brand focus:outline-none"
                placeholder={t('PERSONALIZE_IDENTITY_DOCUMENT_PLACEHOLDER')}
                value={identityDocument}
                onChange={(e) => setIdentityDocument(e.target.value)}
              />
            </>
          )}
        </div>
      )}

      {/* Peru Billing: Boleta / Factura */}
      {showPeruBilling && (
        <div className="space-y-4">
          <p className="text-sm font-medium text-foreground/70">{t('SELECT_BILLING_OPTION')}</p>
          <div className="flex" role="group" aria-label={t('SELECT_BILLING_OPTION')}>
            <button
              type="button"
              aria-pressed={billType === 'boleta'}
              className={`px-5 py-2 rounded-l-xl border transition-all duration-200 ${
                billType === 'boleta'
                  ? 'bg-brand text-brand-foreground border-brand shadow-sm'
                  : 'bg-card text-foreground/70 border-border hover:border-brand-light'
              }`}
              onClick={() => setBillType('boleta')}
            >
              {t('PERSONAL_BILL')}
            </button>
            <button
              type="button"
              aria-pressed={billType === 'factura'}
              className={`px-5 py-2 rounded-r-xl border transition-all duration-200 ${
                billType === 'factura'
                  ? 'bg-brand text-brand-foreground border-brand shadow-sm'
                  : 'bg-card text-foreground/70 border-border hover:border-brand-light'
              }`}
              onClick={() => setBillType('factura')}
            >
              {t('BUSINESS_BILL')}
            </button>
          </div>

          {billType === 'boleta' ? (
            <div className="space-y-3">
              <label htmlFor="bill-personal-name" className="sr-only">{t('SIGNUP_LABEL_NAME')}</label>
              <input
                id="bill-personal-name"
                type="text"
                aria-required="true"
                className="w-full rounded-xl border border-border p-3 text-foreground focus:border-brand focus:outline-none"
                placeholder={t('SIGNUP_LABEL_NAME')}
                value={billPersonalName}
                onChange={(e) => setBillPersonalName(e.target.value)}
              />
              <label htmlFor="bill-personal-document" className="sr-only">{t('PERSONAL_ID_NUMBER')}</label>
              <input
                id="bill-personal-document"
                type="text"
                aria-required="true"
                className="w-full rounded-xl border border-border p-3 text-foreground focus:border-brand focus:outline-none"
                placeholder={t('PERSONAL_ID_NUMBER')}
                value={billPersonalDocument}
                onChange={(e) => setBillPersonalDocument(e.target.value)}
              />
              <label htmlFor="bill-personal-address" className="sr-only">{t('ADDRESS')}</label>
              <input
                id="bill-personal-address"
                type="text"
                aria-required="true"
                className="w-full rounded-xl border border-border p-3 text-foreground focus:border-brand focus:outline-none"
                placeholder={t('ADDRESS')}
                value={billPersonalAddress}
                onChange={(e) => setBillPersonalAddress(e.target.value)}
              />
            </div>
          ) : (
            <div className="space-y-3">
              <label htmlFor="bill-business-name" className="sr-only">{t('BUSINESS_NAME')}</label>
              <input
                id="bill-business-name"
                type="text"
                aria-required="true"
                className="w-full rounded-xl border border-border p-3 text-foreground focus:border-brand focus:outline-none"
                placeholder={t('BUSINESS_NAME')}
                value={billBusinessName}
                onChange={(e) => setBillBusinessName(e.target.value)}
              />
              <label htmlFor="bill-business-document" className="sr-only">{t('BUSINESS_TAX_ID')}</label>
              <input
                id="bill-business-document"
                type="text"
                aria-required="true"
                className="w-full rounded-xl border border-border p-3 text-foreground focus:border-brand focus:outline-none"
                placeholder={t('BUSINESS_TAX_ID')}
                value={billBusinessDocument}
                onChange={(e) => setBillBusinessDocument(e.target.value)}
              />
              <label htmlFor="bill-business-address" className="sr-only">{t('BUSINESS_ADDRESS')}</label>
              <input
                id="bill-business-address"
                type="text"
                aria-required="true"
                className="w-full rounded-xl border border-border p-3 text-foreground focus:border-brand focus:outline-none"
                placeholder={t('BUSINESS_ADDRESS')}
                value={billBusinessAddress}
                onChange={(e) => setBillBusinessAddress(e.target.value)}
              />
            </div>
          )}
        </div>
      )}
    </Private>
  );
}
